import { StackActions, useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";

import { useViewport } from "@/contexts/ViewportContext";
import { RootNavigation, RootStackParamList } from "@/navigation/Root";
import { isNativeDevice } from "@/utils/platform";

import { useAppSelector } from "./store";

type StacksToNavigate = keyof Omit<RootStackParamList, "tabs">;

const commonStackList: Partial<StacksToNavigate>[] = [
  "homeStack",
  "settingsStack",
  "cartStack",
  "orderStack",
];

type NavigationReturn<T> = {
  goToHome: () => void;
  navigateBetweenStacks: (
    stack: StacksToNavigate,
    route?: string,
    params?: any,
    replaceRoute?: boolean
  ) => void;
} & T;

export function useAppNavigation<T = RootNavigation>(): NavigationReturn<T> {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { isWebMode } = useViewport();
  const { logged } = useAppSelector((store) => store.auth);

  const initialRoute = logged ? (isWebMode ? "homeStack" : "tabs") : "welcome";

  const goBack = () => {
    if (isNativeDevice) {
      return navigation.goBack();
    }
    return navigation.canGoBack()
      ? navigation.goBack()
      : navigation.dispatch(StackActions.replace(initialRoute));
  };

  const goToHome = () => {
    navigation.dispatch(StackActions.replace(initialRoute));
  };

  const navigateBetweenStacks = (
    stack: StacksToNavigate,
    route?: string,
    params = {},
    replaceRoute = false
  ) => {
    const navigateFn = replaceRoute ? navigation.replace : navigation.navigate;
    if (commonStackList.includes(stack)) {
      if (route) {
        return navigateFn(
          (isWebMode ? stack : "tabs") as never,
          (isWebMode
            ? { screen: route, params }
            : {
                screen: stack,
                params: {
                  screen: route,
                  initial: false,
                  params,
                },
              }) as never
        );
      }
      return navigateFn(
        (isWebMode ? stack : "tabs") as never,
        (isWebMode
          ? undefined
          : {
              screen: stack,
              initial: false,
              params,
            }) as never
      );
    }
    return navigateFn(
      stack as never,
      route ? { screen: route, initial: false, params } : (params as never)
    );
  };

  return {
    ...navigation,
    goBack,
    goToHome,
    navigateBetweenStacks,
  } as unknown as NavigationReturn<T>;
}
